import { useNavigate, Link } from 'react-router-dom';
import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import {
  ShieldCheck, ClipboardCheck, MessageSquareText, AlertTriangle, FileSearch, Car,
  Gauge, Wrench, ScanLine, Handshake, ListChecks, CheckCircle, ArrowRight, Star,
  Download, Zap, Facebook, HelpCircle, BookOpen, Lock,
} from 'lucide-react';
import BrandHero from '../components/BrandHero';
import { Seo } from '../components/academy/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_IMG = 'https://customer-assets-lqy194kg.emergentagent.net/job_a458f44e-02cd-4e55-83d8-3e9684068794/artifacts/e6vo0d5c_1CAFEF48-974C-4AFB-8229-26CBF6DE8A38.png';

const INSIDE = [
  { Icon: ListChecks, t: 'The 7-Step Car Buying Roadmap', d: 'A clear path from your first search to the final handshake so you never feel lost or rushed.' },
  { Icon: Gauge, t: 'The Real Budget Builder', d: 'See the true cost of ownership \u2014 taxes, registration, insurance, first repairs \u2014 not just the sticker price.' },
  { Icon: FileSearch, t: 'The 60-Second Listing Screen', d: 'Kill weak listings fast so you stop wasting time on cars that were never worth the drive.' },
  { Icon: MessageSquareText, t: 'Questions to Ask Any Seller', d: 'The exact questions that expose inconsistencies before you ever leave your driveway.' },
  { Icon: AlertTriangle, t: 'Dealer & Private-Seller Red Flags', d: 'Spot the warning signs the pros know \u2014 plus a full marketplace scam-avoidance guide.' },
  { Icon: ScanLine, t: 'Vehicle History Decoder', d: 'Read a Carfax like an inspector \u2014 accidents, title brands, mileage gaps, and service history.' },
  { Icon: Car, t: 'Full Physical Inspection Checklists', d: 'Exterior walkaround, tires & brakes, engine bay, interior, cold start, and the complete test drive.' },
  { Icon: Wrench, t: 'Diagnostics Made Simple', d: 'Dashboard light guide, OBD-II basics, and how to catch flood, water, and hidden accident damage.' },
  { Icon: Handshake, t: 'The Negotiation Worksheet', d: 'Turn what you find into real dollars off the price \u2014 with a script that actually works.' },
  { Icon: ShieldCheck, t: 'When to Walk Away', d: 'A decision scorecard and clear rules so you protect your money instead of talking yourself into a mistake.' },
];

const FOR_YOU = [
  'You found a car online and don\u2019t want to get burned.',
  'You\u2019re shopping dealerships and private sellers and want the upper hand.',
  'You\u2019re not ready to book a full inspection yet \u2014 but you want to shop smart today.',
  'You want a step-by-step system, not vague YouTube advice.',
];

const REVIEWS = [
  { name: 'dexter04', initial: 'D', rating: 5, body: '10 out of 10, will recommend. CarLens took the fear out of buying a used vehicle \u2014 very professional, very thorough, and made the whole process very easy.' },
  { name: 'Mehdi B.', initial: 'M', rating: 5, body: 'Amazing service that I cannot ever imagine buying another used car without. Saved me thousands of dollars and a potential headache.' },
  { name: 'Steve R.', initial: 'S', rating: 5, body: 'The most detailed report I\u2019ve ever seen on the condition of the truck I was interested in buying. Answered all of my questions.' },
];

const FAQS = [
  { q: 'What exactly do I get for $19?', a: 'You get the complete CarLens Ultimate Used Car Buyer Toolkit\u2122 \u2014 a professionally designed digital guide (PDF) with 25+ sections of checklists, questions, red-flag guides, inspection walkthroughs, a negotiation worksheet, and decision scorecards. It\u2019s the same system our inspectors think through, condensed into something you can keep on your phone while you shop.' },
  { q: 'How do I get the toolkit after I pay?', a: 'Instantly. The moment your payment is confirmed you\u2019ll see a secure download button, and we\u2019ll also email you a private download link if you provide your email at checkout. You can save it to your phone and re-download it anytime.' },
  { q: 'Is this a physical book?', a: 'No \u2014 it\u2019s a digital download you can read on your phone, tablet, or computer. That means you have it in your pocket at the dealership, in the driveway of a private seller, or while you\u2019re messaging sellers online.' },
  { q: 'Do I need to book an inspection to use this?', a: 'Not at all. The toolkit is built to help you shop smarter on your own. If you later find a car you\u2019re serious about, a CarLens FirstLook\u2122 consultation or a full pre-purchase inspection takes the guesswork out completely \u2014 but the toolkit stands on its own.' },
  { q: 'Can I use this anywhere, or only in Florida?', a: 'Anywhere. The buying process, red flags, inspection checklists, and negotiation tactics apply to any used car in any state. Our in-person inspections are Northeast Florida only \u2014 but the toolkit is for every buyer.' },
];

const PRODUCT_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'CarLens Ultimate Used Car Buyer Toolkit',
  description: 'A digital step-by-step used car buying guide with inspection checklists, seller questions, red-flag guides, negotiation worksheets, and decision scorecards.',
  brand: { '@type': 'Brand', name: 'CarLens Inspections' },
  offers: {
    '@type': 'Offer', priceCurrency: 'USD', price: '19', availability: 'https://schema.org/InStock',
    url: 'https://carlensauto.com/used-car-buyer-toolkit',
  },
};

const BREADCRUMBS = [
  { name: 'Home', path: '/' },
  { name: 'Used Car Buyer Toolkit', path: '/used-car-buyer-toolkit' },
];

const UsedCarBuyerToolkit = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const startCheckout = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/toolkit/checkout`, { email: email.trim() || null });
      if (res.data && res.data.url) {
        window.location.href = res.data.url;
      } else {
        setError('Could not start checkout. Please try again.');
        setLoading(false);
      }
    } catch (e) {
      setError('Something went wrong starting checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }} data-testid="toolkit-page">
      <Seo
        siteName="CarLens"
        title="Ultimate Used Car Buyer Toolkit \u2014 $19 Instant Download"
        description="Not ready for an inspection yet? Shop smarter with the CarLens Ultimate Used Car Buyer Toolkit\u2122 \u2014 your step-by-step guide to spotting red flags, asking the right questions, and knowing when to walk away. Instant access, $19."
        path="/used-car-buyer-toolkit"
        faq={FAQS}
        breadcrumbs={BREADCRUMBS}
        extraSchemas={[PRODUCT_SCHEMA]}
      />

      <BrandHero />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 pt-10 pb-16 md:pt-14" data-testid="toolkit-hero">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-orange-500/40 bg-orange-500/10 px-4 py-1.5 mb-6">
              <Zap className="w-4 h-4 text-orange-400" />
              <span className="text-orange-300 text-sm font-semibold tracking-wide">Digital Toolkit &bull; Instant Access</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.06]" style={{ fontFamily: 'Space Grotesk' }}>
              Not Ready for an Inspection Yet?
            </h1>
            <p className="mt-6 text-lg md:text-xl text-zinc-300 leading-relaxed">
              Shop smarter with the <strong className="text-white">CarLens Ultimate Used Car Buyer Toolkit&trade;</strong> &mdash; your step-by-step guide to spotting red flags, asking the right questions, and knowing when to walk away.
            </p>

            <div className="mt-8 rounded-2xl border border-zinc-800 bg-[#161618]/80 p-5 max-w-md">
              <div className="flex items-end gap-3 mb-4">
                <span className="text-4xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>$19</span>
                <span className="text-zinc-400 text-sm mb-1.5">one-time &bull; instant download</span>
              </div>
              <label htmlFor="toolkit-email" className="block text-sm text-zinc-400 mb-2">Email (optional &mdash; we&rsquo;ll send your download link)</label>
              <input
                id="toolkit-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full rounded-lg bg-zinc-900 border border-zinc-700 px-4 py-3 text-white placeholder-zinc-500 focus:border-orange-500 focus:outline-none mb-4"
                data-testid="toolkit-email-input"
              />
              <Button
                size="lg"
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-6 text-lg font-semibold rounded-lg shadow-lg shadow-orange-500/20 disabled:opacity-60"
                onClick={startCheckout}
                data-testid="toolkit-hero-buy-btn"
              >
                {loading ? 'Starting checkout\u2026' : 'Get Instant Access \u2014 $19'}
              </Button>
              {error && <p className="mt-3 text-sm text-red-400" data-testid="toolkit-error">{error}</p>}
              <div className="mt-4 flex items-center justify-center gap-4 text-xs text-zinc-500">
                <span className="inline-flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> Secure checkout</span>
                <span className="inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" /> Instant delivery</span>
              </div>
            </div>
            <p className="mt-5 text-sm text-zinc-400 tracking-wide">
              25+ sections &bull; Checklists &bull; Seller scripts &bull; Red-flag guides &bull; Negotiation worksheet
            </p>
          </div>

          <div className="relative mx-auto w-full max-w-md">
            <div className="absolute -inset-4 bg-gradient-to-r from-orange-500/25 to-zinc-600/20 rounded-3xl blur-3xl" />
            <img
              src={PRODUCT_IMG}
              alt="CarLens Ultimate Used Car Buyer Toolkit cover"
              className="relative rounded-2xl shadow-2xl border-2 border-zinc-700/60 w-full h-auto"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* PROBLEM / PROMISE */}
      <section className="bg-gradient-to-b from-zinc-50 to-white py-24" data-testid="toolkit-promise">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Space Grotesk' }}>
            Most People Buy a Used Car on Hope. You Don&rsquo;t Have To.
          </h2>
          <p className="mt-5 text-zinc-600 text-lg leading-relaxed">
            A bad used car can cost you thousands in repairs, weeks of stress, and a hit to your budget you didn&rsquo;t plan for. The toolkit gives you the exact system a professional inspector runs through &mdash; so you walk into every deal informed, calm, and in control.
          </p>
          <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-orange-500/10 border border-orange-500/30 px-5 py-2.5">
            <ShieldCheck className="w-5 h-5 text-orange-500" />
            <span className="text-zinc-800 font-semibold">$19 &mdash; a fraction of one bad decision.</span>
          </div>
        </div>
      </section>

      {/* WHAT'S INSIDE */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="toolkit-inside">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 mb-3">
            <BookOpen className="w-6 h-6 text-orange-500" />
            <span className="text-orange-400 font-semibold tracking-wide uppercase text-sm">What&rsquo;s Inside</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>Everything You Need to Buy With Confidence</h2>
          <p className="text-zinc-400 text-base md:text-lg max-w-2xl mx-auto">A complete, professionally designed guide you keep on your phone while you shop, message sellers, and inspect vehicles.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-5">
          {INSIDE.map((it, i) => (
            <div key={i} className="flex items-start gap-4 rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 p-6 hover:border-orange-500/50 transition-colors" data-testid={`toolkit-inside-${i}`}>
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center shrink-0">
                <it.Icon className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <h3 className="text-white font-bold leading-snug mb-1">{it.t}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{it.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="max-w-5xl mx-auto px-6 py-16" data-testid="toolkit-foryou">
        <div className="rounded-3xl border border-zinc-800 bg-[#161618] p-10 md:p-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center" style={{ fontFamily: 'Space Grotesk' }}>This Is For You If&hellip;</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {FOR_YOU.map((t, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`toolkit-for-${i}`}>
                <CheckCircle className="w-6 h-6 text-orange-400 flex-shrink-0 mt-0.5" />
                <span className="text-zinc-200 text-base md:text-lg leading-snug">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="toolkit-reviews">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-1 mb-3">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 fill-orange-400 text-orange-400" />)}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>Trusted by Real Florida Buyers</h2>
          <p className="text-zinc-400 mt-3">The same team behind the toolkit inspects hundreds of vehicles for buyers just like you.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {REVIEWS.map((r, i) => (
            <div key={i} className="rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 p-6" data-testid={`toolkit-review-${i}`}>
              <div className="flex items-center gap-1 mb-4">
                {[...Array(r.rating)].map((_, j) => <Star key={j} className="w-4 h-4 fill-orange-400 text-orange-400" />)}
              </div>
              <p className="text-zinc-300 text-sm leading-relaxed mb-5">&ldquo;{r.body}&rdquo;</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-300 font-bold">{r.initial}</div>
                <span className="text-white font-medium">{r.name}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-20" data-testid="toolkit-faq">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-3">
            <HelpCircle className="w-6 h-6 text-orange-500" />
            <span className="text-orange-400 font-semibold tracking-wide uppercase text-sm">FAQ</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>Toolkit Questions</h2>
        </div>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <details key={i} className="group rounded-2xl border border-zinc-800 bg-[#161618] px-6 py-5 hover:border-orange-500/40 transition-colors" data-testid={`toolkit-faq-${i}`}>
              <summary className="flex items-center justify-between cursor-pointer list-none text-white font-semibold text-base md:text-lg">
                <span>{f.q}</span>
                <ArrowRight className="w-5 h-5 text-orange-400 flex-shrink-0 ml-4 transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-4 text-zinc-300 text-base leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="toolkit-final-cta">
        <div className="bg-gradient-to-r from-orange-600/20 to-orange-500/20 rounded-3xl border border-orange-500/30 p-10 md:p-14 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: 'Space Grotesk' }}>
            Shop Your Next Used Car Like a Pro.
          </h2>
          <p className="text-zinc-300 text-lg mb-8 max-w-2xl mx-auto">
            Get instant access to the CarLens Ultimate Used Car Buyer Toolkit&trade; and never walk into a deal blind again.
          </p>
          <Button
            size="lg"
            disabled={loading}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-10 py-6 text-lg font-semibold rounded-lg shadow-lg disabled:opacity-60"
            onClick={startCheckout}
            data-testid="toolkit-final-buy-btn"
          >
            {loading ? 'Starting checkout\u2026' : 'Get Instant Access \u2014 $19'}
          </Button>
          <p className="text-zinc-400 text-sm mt-6">
            Found a car you&rsquo;re serious about?{' '}
            <Link to="/firstlook" className="text-orange-400 hover:text-orange-300 font-medium">Book a CarLens FirstLook&trade;</Link>{' '}or a{' '}
            <Link to="/book" className="text-orange-400 hover:text-orange-300 font-medium">full inspection</Link>.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 mt-8">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-white font-semibold tracking-wide" style={{ fontFamily: 'Space Grotesk' }}>CarLens Automotive Intelligence</p>
              <p className="text-zinc-500 text-sm">&copy; 2025 CarLens Automotive Intelligence. All rights reserved.</p>
            </div>
            <a href="https://www.facebook.com/profile.php?id=61583275422342" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-zinc-400 hover:text-zinc-200 transition-colors">
              <Facebook className="w-5 h-5" />
              <span>Follow us on Facebook</span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UsedCarBuyerToolkit;
